import { t } from '@shared/i18n';
import { CustomVocabularyService, CustomVocabularyWord, Language } from '@shared/openapi/jaaluu-api';
import sharedStores from '@shared/shared-stores';
import { v4 as uuidv4 } from 'uuid';
import { ref, Ref } from 'vue';
import { language } from '.';

const CUSTOM_VOCABULARY_LISTS_KEY = 'customVocabularyLists';
const CUSTOM_VOCABULARY_INDEX_KEY = 'customVocabularyIndex';

export interface CustomVocabularyList {
  id: string;
  title: string;
  description: string;
  nWords: number;
  words: CustomVocabularyWord[];
  createdAt: string;
}

export default () => {
  const { langForeign, langNative } = language.state;
  const { notify } = sharedStores.snackbar.funcs;

  const customVocabularyListsLangs: Ref<Record<string, CustomVocabularyList[]>> = ref({});
  const customVocabularyLists: Ref<CustomVocabularyList[]> = ref([]);
  const customVocabularyIndexStates: Ref<Record<string, number>> = ref({});
  const isGeneratingCustomVocabulary = ref(false);

  const getLangKey = (): string => `${langForeign.value}_${langNative.value}`;

  const saveCustomVocabularyLists = () => {
    customVocabularyListsLangs.value[getLangKey()] = customVocabularyLists.value;
    localStorage.setItem(
      CUSTOM_VOCABULARY_LISTS_KEY,
      JSON.stringify(customVocabularyListsLangs.value)
    );
  };

  const saveCustomVocabularyIndexStates = () => {
    localStorage.setItem(
      CUSTOM_VOCABULARY_INDEX_KEY,
      JSON.stringify(customVocabularyIndexStates.value)
    );
  };

  const loadCustomVocabularyLists = () => {
    if (!langForeign.value || !langNative.value) {
      customVocabularyLists.value = [];
      return;
    }
    const stored = localStorage.getItem(CUSTOM_VOCABULARY_LISTS_KEY);
    if (stored) {
      try {
        customVocabularyListsLangs.value = JSON.parse(stored);
      } catch (error) {
        console.error('Error loading custom vocabulary lists:', error);
      }
    }
    const storedIndex = localStorage.getItem(CUSTOM_VOCABULARY_INDEX_KEY);
    if (storedIndex) {
      try {
        customVocabularyIndexStates.value = JSON.parse(storedIndex);
      } catch (error) {
        console.error('Error loading custom vocabulary index states:', error);
      }
    }
    customVocabularyLists.value = customVocabularyListsLangs.value[getLangKey()] || [];
  };

  const getCustomVocabularyList = (id: string): CustomVocabularyList | undefined => {
    return customVocabularyLists.value.find((l) => l.id === id);
  };

  const generateCustomVocabularyList = (
    title: string,
    description: string,
    nWords: number
  ): Promise<CustomVocabularyList | null> => {
    if (!langForeign.value || !langNative.value) return Promise.resolve(null);
    const foreign: Language = langForeign.value;
    const native: Language = langNative.value;
    isGeneratingCustomVocabulary.value = true;
    return CustomVocabularyService.postCustomVocabularyV1CustomVocabularyPost({
      body: {
        title,
        description,
        n_words: nWords,
        foreign,
        native
      },
      throwOnError: true
    })
      .then((res) => {
        const list: CustomVocabularyList = {
          id: uuidv4(),
          title,
          description,
          nWords,
          words: res.data?.words ?? [],
          createdAt: new Date().toISOString()
        };
        customVocabularyLists.value.push(list);
        saveCustomVocabularyLists();
        return list;
      })
      .catch((error) => {
        console.error('Error generating custom vocabulary list:', error);
        notify({
          type: 'error',
          text: t('custom_vocabulary.error_generating'),
          icon: 'mdi-alert'
        });
        return null;
      })
      .finally(() => {
        isGeneratingCustomVocabulary.value = false;
      });
  };

  const deleteCustomVocabularyList = (id: string) => {
    customVocabularyLists.value = customVocabularyLists.value.filter((l) => l.id !== id);
    saveCustomVocabularyLists();
    delete customVocabularyIndexStates.value[id];
    saveCustomVocabularyIndexStates();
  };

  const setCustomVocabularyIndex = (id: string, index: number) => {
    customVocabularyIndexStates.value[id] = index;
    saveCustomVocabularyIndexStates();
  };

  const getCustomVocabularyIndex = (id: string): number => {
    return customVocabularyIndexStates.value[id] ?? 0;
  };

  return {
    state: {
      customVocabularyLists,
      isGeneratingCustomVocabulary
    },
    funcs: {
      loadCustomVocabularyLists,
      getCustomVocabularyList,
      generateCustomVocabularyList,
      deleteCustomVocabularyList,
      setCustomVocabularyIndex,
      getCustomVocabularyIndex
    }
  };
};
